import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useAuth } from "/imports/state/AuthContext";
import api from "/imports/api/client";
import { applyBranding } from "/imports/libs/branding";
import { createWidgetTheme } from "/imports/ui/styles/theme";

// Read the slug from the reset token's payload so the page can adopt the club's
// branding before submit. This is a non-verified decode for theming only — the
// server still validates the signature on reset.
function readSlugFromToken(token) {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json).slug || "";
  } catch {
    return "";
  }
}

function ResetPasswordView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [brandColor, setBrandColor] = useState(null);
  const theme = useMemo(() => createWidgetTheme({ primaryColor: brandColor || undefined }), [brandColor]);

  useEffect(() => {
    const slug = readSlugFromToken(token);
    if (!slug) return;
    api
      .getWidgetConfig(slug)
      .then((res) => {
        setBrandColor(res?.widget?.color || null);
        applyBranding({ name: res?.saas?.name || res?.widget?.name, logoUrl: res?.saas?.logoUrl || res?.widget?.logoUrl });
      })
      .catch(() => {});
  }, [token]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError("Please choose a password");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const data = await resetPassword(token, password);
      const target = data?.slug ? `/${data.slug}/login` : "/";
      navigate(target, {
        replace: true,
        state: { flash: { message: "Password reset. Please sign in with your new password.", variant: "success" }, email: data?.email || "" }
      });
    } catch (err) {
      setError(err.message || "We couldn't reset your password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" p={3}>
        <Card sx={{ maxWidth: "28.75rem", width: "100%" }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
            <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
              Set a new password
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
              Choose a new password for your account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                autoComplete="new-password"
                helperText="At least 6 characters"
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        onMouseDown={(ev) => ev.preventDefault()}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                margin="normal"
              />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={submitting || !password || !confirmPassword} sx={{ mt: 3 }}>
                {submitting ? "Resetting..." : "Reset password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}

export default ResetPasswordView;
